import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Table, Button, Dropdown, Menu, Modal } from "antd";
import { MoreVertical } from "lucide-react";
import NameAvatar from "./NameAvatar";
import TeachersObservation from "../../modals/teacher/TeachersObservation";
import { useTranslation } from "react-i18next";
import { postRequest } from "../../services";
import { message } from "antd";

const AttendanceTable = forwardRef((props, ref) => {
  const { myAttendaceData, classData, fetchAttendanceData,classCurriculum } = props;

  const [transformedData, setTransformedData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "sa";

  useEffect(() => {
    if (myAttendaceData.students && classData) {
      const newData = myAttendaceData.students.map((student) => ({
        key: student.id,
        name: student.name,
        dp: student.profilePicture,
        section: classData.sectionName || "Unknown",
        attendance: [
          { label: "PP", checked: false },
          { label: "PO", checked: false },
          { label: "A", checked: false },
          { label: "L", checked: false },
        ],
        performance: `${student.attendancePercentage}%`,
      }));
      setTransformedData(newData);
    }
  }, [myAttendaceData, classData]);

  const handleAttBtnClick = (index, name) => {
    const updatedData = transformedData.map((student) => {
      if (student.name === name) {
        student.attendance = student.attendance.map((att, i) => ({
          ...att,
          checked: i === index,
        }));

        const updatedAttendanceData = attendanceData.filter((data) => data.studentId !== student.id);
        updatedAttendanceData.push({
          studentId: student.id,
          status: student.attendance.find((att) => att.checked).label,
        });
        setAttendanceData(updatedAttendanceData);
      }
      return student;
    });
    setTransformedData(updatedData);
  };

  const createAttendanceButtons = (attendance, name) => {
    return attendance.map((item, index) => (
      <Button
        key={index}
        type={item.checked ? "primary" : "default"}
        onClick={() => handleAttBtnClick(index, name)}
      >
        {item.label}
      </Button>
    ));
  };

  const triggerSaveAttendance = async () => {
    const date = new Date();
    const formattedDate = date.toISOString();
    const payload = {
      subjectId: classData.subjectId,
      classAssignmentId: classData.id,
      date: formattedDate,
      students: attendanceData,
    };
    try {
      const response = await postRequest(`/api/Attendance/mark`, payload);
      if (response.status === 200 && response.data.success) {
        message.success(response.data.message || "Attendance saved successfully");
        fetchAttendanceData();
      } else {
        message.error(response.data.message || "Failed to save lesson");
      }
    } catch (error) {
      console.error("Error:", error.message);
      message.error("An error occurred while saving the lesson");
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSaveAttendance,
  }));

  const columns = [
    {
      title: t("studentName"),
      dataIndex: "name",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          {record.dp ? <img src={record.dp} alt="user" style={{ borderRadius: '50%', width: '30px', height: '30px' }} /> : <NameAvatar name={text} rounded />}
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: t("Section"),
      dataIndex: "section",
    },
    {
      title: t("markAttendance"),
      dataIndex: "attendance",
      render: (attendance, record) => (
        <div className="d-flex gap-2">
          {createAttendanceButtons(attendance, record.name)}
        </div>
      ),
    },
    {
      title: t("overallAttendance"),
      dataIndex: "performance",
    },
    {
      title: t("action"),
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="1" onClick={() => setOpen(true)}>
                Teacher's Observation
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <Button icon={<MoreVertical />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Modal visible={open} onCancel={() => setOpen(false)} footer={null}>
        <TeachersObservation onRequestClose={() => setOpen(false)} classCurriculum={classCurriculum} />
      </Modal>
      <Table
        columns={columns}
        dataSource={transformedData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        rowClassName={(record, index) => (index % 2 === 0 ? 'even-row' : 'odd-row')}
        dir={isArabic ? "rtl" : "ltr"}
      />
    </>
  );
});

export default AttendanceTable;
